import React, { FC } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { get } from 'lodash';
import { FormDataType } from 'contexts/FormProvider';
import { useContentContext } from '@qbe/dfc-content';
import { Button, FormGroupLabel } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { InformationPopUp, FormSpacer, CmsAddressSearch } from 'components';

import { YesNoList } from 'utils/constants';
import { phoneNumberSchema } from 'pages/Complaint/InsuredDetails/schema';
import ContactPreferredMethod from 'pages/common/components/ContactPreferredMethod';
import { AddressAutocomplete } from 'pages/common/components/AddressAutocomplete';
import { contactPreferredFields } from '../enum';
import { ContactNumber, TextFieldController, ToggleController } from 'controllers';
import { AddressManual } from 'controllers/AddressManual';
import { useContentComplaintPartners } from 'utils/content';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContactAddressHandlers } from 'hooks/useContactAddressHandlers';
import { AddressType } from 'components/AddressSearch/addressTypes';

interface Props {
  form: UseFormReturn<FormDataType>;
  show: boolean;
  showNextButton: boolean;
  isContactDetailsValid: boolean;
  isFieldRequired: (fieldName: string) => boolean;
}

type PreferredMethod = 'email' | 'phone' | 'post';

const ContactDetails: FC<Props> = (props) => {
  const { form, show, showNextButton = true, isContactDetailsValid = false, isFieldRequired } = props;

  const { register, watch, getValues, setValue, trigger, clearErrors, control, setError } = form;

  const { DcomPrtrQasMigrationToggle = true } = useFlags();

  const {
    handleRepPostalAddressChange,
    handlePhysicalAddressChange,
    getRepPostalAddressDefaults,
    getPhysicalAddressDefaults,
    isManualAddressRes,
    isManualAddressPhy,
  } = useContactAddressHandlers({ form, qasMigrationEnabled: DcomPrtrQasMigrationToggle });

  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  const TEXT_CONTENT = {
    header: getTextContent('fha.contact.details.header'),
    preferredContact: getTextContent('fha.contact.details.preferredContact'),
    preferredContactInfo: getTextContent('fha.contact.details.preferredContactInfo'),
    email: getTextContent('fha.contact.details.email'),
    physicalAddress: getTextContent('fha.contact.details.physicalAddress'),
    addressType: getTextContent('fha.contact.details.addressType'),
    contactNumber: getTextContent('fha.contact.details.contactNumber'),
  };

  const values: object = watch();

  const addressType = watch('addressType') || '';
  const mPhoneNumber = watch('mPhoneNumber') || '';
  const hPhoneNumber = watch('hPhoneNumber') || '';
  const oPhoneNumber = watch('oPhoneNumber') || '';
  const iContactPreferedMethod = watch('iContactPreferedMethod') || '';

  const showPostalAddress = addressType === 'no';

  const requiredChecker = (field: PreferredMethod) => (iContactPreferedMethod === field ? ` *` : '');

  React.useEffect(() => {
    const haveOneValidNumber = [mPhoneNumber, hPhoneNumber, oPhoneNumber].some((phoneNumber) =>
      phoneNumberSchema(false).isValidSync(phoneNumber)
    );

    setValue('haveOneValidNumber', haveOneValidNumber);

    if (iContactPreferedMethod === 'phone' && !haveOneValidNumber) {
      setError('haveOneValidNumber', { type: 'required', message: 'must have one valid number' });
    } else clearErrors('haveOneValidNumber');
  }, [hPhoneNumber, mPhoneNumber, oPhoneNumber, iContactPreferedMethod, setValue, clearErrors, setError]);

  const handleContactMethod = (contactMethod: keyof FormDataType, data: string[]) => (method: string) => {
    setValue(contactMethod, method);

    data.forEach((field: string) => {
      if (get(values, field) && get(values, field).length > 0) {
        trigger(field as any);
      } else {
        clearErrors(field as any);
      }

      return field;
    });
  };

  if (!show) return null;

  return (
    <FormCard variant="sectionTitle" title={TEXT_CONTENT.header}>
      {/** iContactPreferedMethod */}
      <input type="hidden" {...register('iContactPreferedMethod')} />
      <ContactPreferredMethod
        name="iContactPreferedMethod"
        title={TEXT_CONTENT.preferredContact}
        titleSuffix={<InformationPopUp message={TEXT_CONTENT.preferredContactInfo} />}
        value={iContactPreferedMethod}
        onChange={handleContactMethod('iContactPreferedMethod', contactPreferredFields)}
      />

      <FormSpacer type="input" />

      <FormGroupLabel alignCenter required={isFieldRequired('cEmailAddress')} title={TEXT_CONTENT.email} />
      <FormSpacer type="input" />
      <TextFieldController
        control={control}
        name="cEmailAddress"
        label="Email address"
        maxLength={100}
        inputProps={{
          maxLength: 100,
          ['data-track']: 'emailAddressCustomers',
          ['data-track-value']: 'mask',
        }}
      />

      {/** physical address */}
      <FormGroupLabel alignCenter required title={TEXT_CONTENT.physicalAddress} />
      <FormSpacer type="input" />
      {DcomPrtrQasMigrationToggle ? (
        <CmsAddressSearch
          type={AddressType.RepPostal}
          required={true}
          onAddressChange={handleRepPostalAddressChange}
          getAddressDefaults={getRepPostalAddressDefaults}
        />
      ) : (
        <>
          {!isManualAddressRes && (
            <AddressAutocomplete
              form={form}
              label="Residential address"
              fieldName="residentialAddress"
              fieldNameIsValid="isResAddressValid"
              fieldNameIsManual="isManualAddressRes"
              dataTrack="residentialAddress"
              dataTrackValue="mask"
              linkType="residential"
              addressFieldNames={{
                country: 'rCountry',
                streetAddress: 'rStreetAddress',
                suburb: 'rSuburb',
                state: 'rState',
                postCode: 'rPostcode',
              }}
            />
          )}

          {isManualAddressRes && <AddressManual prefix="r" form={form} />}
        </>
      )}
      {/** addressType */}

      <ToggleController
        control={control}
        name="addressType"
        required={isFieldRequired('addressType')}
        title={`${TEXT_CONTENT.addressType}`}
        options={YesNoList}
        capture
        onAfterChange={(e) => {
          const { value } = e.target as any;
          if (value === 'yes') {
            setValue('cPostalAddress', null, { shouldValidate: true });
            setValue('isManualAddressPhy', false, { shouldValidate: true });
            setValue('cPostalAddressObject', null, { shouldValidate: true });
            setValue('pStreetAddress', '', { shouldValidate: true });
            setValue('pSuburb', '', { shouldValidate: true });
            setValue('pCountry', '', { shouldValidate: true });
            setValue('pState', '', { shouldValidate: true });
            setValue('pPostcode', '', { shouldValidate: true });
          }
        }}
      />

      {showPostalAddress && <FormSpacer type="input" />}

      {/** postalAddress */}
      {DcomPrtrQasMigrationToggle && showPostalAddress ? (
        <CmsAddressSearch
          type={AddressType.Physical}
          required={true}
          onAddressChange={handlePhysicalAddressChange}
          getAddressDefaults={getPhysicalAddressDefaults}
        />
      ) : (
        <>
          {showPostalAddress && !isManualAddressPhy && (
            <>
              <>
                <AddressAutocomplete
                  form={form}
                  label="Postal address"
                  fieldName="cPostalAddress"
                  fieldNameIsValid="isPhyAddressValid"
                  fieldNameIsManual="isManualAddressPhy"
                  linkType="physical"
                  addressFieldNames={{
                    country: 'pCountry',
                    streetAddress: 'pStreetAddress',
                    suburb: 'pSuburb',
                    state: 'pState',
                    postCode: 'pPostcode',
                  }}
                  dataTrack="cPostalAddress"
                  dataTrackValue="mask"
                />
              </>
            </>
          )}

          {showPostalAddress && isManualAddressPhy && <AddressManual prefix="p" form={form} />}
        </>
      )}
      {/** contactNumber */}

      <FormGroupLabel alignCenter required={!!requiredChecker('phone')} title={TEXT_CONTENT.contactNumber} />
      <FormSpacer type="input" />
      <ContactNumber form={form as any} />

      {showNextButton && (
        <>
          <FormSpacer type="formgroup" />
          <Button type="submit" label="Continue" disabled={!isContactDetailsValid} />
        </>
      )}
    </FormCard>
  );
};

export default ContactDetails;
